import type {
  SnackBarConfig,
} from '@shared/components/Snackbar';
import type { BasicReview, ReviewSearchType } from '@shared/domain/review';
import {
  CardContent,
  Chip,
  Divider as MuiDivider,
  Tooltip,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AuthorBadge from '@shared/components/AuthorBadge';
import Button from '@shared/components/Buttons/Button';
import CompanyLogo from '@shared/components/CompanyLogo';
import DynamicIcon, { IconName } from '@shared/components/Icon';
import CustomSnackbar from '@shared/components/Snackbar';
import TypedCard from '@shared/components/TypedCard';
import { UserContext } from '@/shared/contexts/UserContext';
import {
  ReviewDepth,
  ReviewStatus,
} from '@shared/domain/review';
import { ReviewStatusChip } from '../../../components/ReviewStatusChip';
import { useApi } from '@shared/services/api';
import { MOBILE_BREAKPOINT } from '@shared/styles/Breakpoints';
import './ReviewCard.scss';

// Editorial cornflower-blue accent (matches `$rv-accent-text` in
// _review-theme.scss). Used for the TypedCard's corner chip on
// network-ui review cards so it sits in the same palette as the
// rest of the redesigned chrome (navbar, side drawer, dashboard).
const REVIEW_CARD_TYPE_COLOR = '#0b74da';

const StyledTypedCard = styled(TypedCard)`
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  padding: 12px;
`;

const CompanyName = styled.div`
  font-weight: 700;
  font-size: 1.0625rem;
  line-height: 1.2;
  max-width: 240px;
  word-break: break-word;
  white-space: normal;
`;

const Grid2x2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
`;

const SectionTitle = styled(Typography).attrs({ variant: 'subtitle1' })`
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
`;

const Label = styled(Typography).attrs({
  variant: 'caption',
  color: 'textSecondary',
})``;

const Value = styled(Typography).attrs({ variant: 'body2' })`
  font-weight: 500;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  overflow: auto;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    padding: var(--spacing-sm);
    gap: var(--spacing-sm);
    align-items: center;
    text-align: center;
  }
`;

const Section = styled.div`
  flex: 1 1 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    width: 100%;
    align-items: center;
    text-align: center;
  }
`;

const AuthorSection = styled(Section)`
  justify-content: flex-start;
`;

const ButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 160px;
  max-width: 180px;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    min-width: 100%;
    max-width: 100%;
    width: 100%;
    align-items: center;
    gap: 8px;

    & > div {
      width: 100%;
    }
    & > button {
      width: 100%;
    }
  }
`;

const Divider = styled(MuiDivider)`
  margin: 0 8px;
  height: auto;
  align-self: stretch;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    width: 100%;
    height: 1px;
    margin: var(--spacing-xs) 0;
  }
`;

const CompanyInfoOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    gap: 10px;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    gap: 8px;
  }
`;

const CompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @container (inline-size < ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    gap: 8px;
  }
`;

interface ReviewCardProps {
  review: BasicReview;
  index: number;
  onGoToReview?: () => void;
  onGotoCompany: () => void;
  userId: number | undefined;
  filterType: ReviewSearchType;
  showTypeIndicator?: boolean;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  onGoToReview,
  onGotoCompany,
  showTypeIndicator,
}) => {
  const { user } = useContext(UserContext);
  const api = useApi();
  const _navigate = useNavigate();

  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const [isFollowed, setIsFollowed] = useState<boolean>(() =>
    Boolean(user?.followedCompanies?.some(c => c?.id === review.companyId)),
  );

  const getDealAreaIcon = (): IconName => {
    const map: { [key: string]: IconName } = {
      'HR': IconName.People,
      'Legal': IconName.Gavel,
      'IT': IconName.Computer,
      'Security': IconName.Lock,
      'Engineering': IconName.Timeline,
      'Product': IconName.Edit,
      'Sales': IconName.Money,
      'Finance': IconName.Money,
      'Operations': IconName.Sync,
      'Marketing': IconName.Chat,
      'Customer Success': IconName.Star,
    };
    return map[review.dealArea || ''] || IconName.Info;
  };

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const followCompany = async () => {
    if (!user)
      return;

    const updateCompanyInput = {
      companyId: review.companyId,
      userId: user.id,
    };

    try {
      await api.post(
        isFollowed
          ? `users/unfollow-company`
          : `users/follow-company`,
        updateCompanyInput,
      );

      setIsFollowed(prev => !prev);
      setSnackbarConfig({
        type: 'success',
        message: `${review.companyName} has been ${
          isFollowed ? 'unfollowed' : 'followed'
        } successfully!`,
        open: true,
      });
    }
    catch (error) {
      console.error('Failed to follow/unfollow company', error);
      setSnackbarConfig({
        type: 'error',
        message: `Something went wrong. Please try again.`,
        open: true,
      });
    }
  };

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.01 }}
        style={{ cursor: 'pointer' }}
        onClick={() => onGoToReview?.()}
      >
        <StyledTypedCard
          data-testid="review-card"
          typeName={showTypeIndicator ? 'Deal Retro' : undefined}
          typeColor={showTypeIndicator ? REVIEW_CARD_TYPE_COLOR : undefined}
          typeDescription="Deal Retros are first-hand experiences shared by Kandir users about companies they have sold to."
        >
          <CardContent>
            <CardRow>
              <Section className="review-card-company-info-section">
                <CompanyInfoOuterContainer>
                  <CompanyInfo className="review-card-company-info">
                    <CompanyHeader>
                      <Tooltip title={`Go to ${review.companyName} company page`} arrow>
                        <CompanyLogo
                          companyId={review.companyId}
                          companyName={review.companyName ?? ''}
                          alt={review.companyName}
                          sx={{ 'width': 52, 'height': 52, 'transition': 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.1)' }, 'cursor': 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onGotoCompany();
                          }}
                        />
                      </Tooltip>
                      <div>
                        <CompanyName data-testid="review-company-name">
                          {review.reviewDepth === ReviewDepth.STUB
                            ? `Deal Insight for ${review.companyName || 'Unknown Company'}`
                            : `Deal Retro for ${review.companyName}`}
                        </CompanyName>
                        <Typography variant="caption" color="text.secondary">
                          First-hand experience from a Kandir user
                        </Typography>
                      </div>
                    </CompanyHeader>
                  </CompanyInfo>
                  {review.reviewDepth === ReviewDepth.STUB ? (
                    <Chip
                      label="Deal Insight"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.12)',
                        color: '#2e7d32',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        fontWeight: 600,
                      }}
                    />
                  ) : (review.status === ReviewStatus.IN_PROGRESS || review.status === ReviewStatus.NEEDS_EDITING) ? (
                    <Chip
                      label="In Progress"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 193, 7, 0.12)',
                        color: '#f57c00',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        fontWeight: 600,
                      }}
                    />
                  ) : user?.isAdmin && review.status !== ReviewStatus.COMPLETED && (
                    <ReviewStatusChip status={review.status} />
                  )}
                </CompanyInfoOuterContainer>
              </Section>

              <Divider orientation="vertical" flexItem />

              <Section className="review-card-deal-info-section">
                <SectionTitle>Deal Info</SectionTitle>
                <Grid2x2>
                  {[
                    {
                      icon: getDealAreaIcon(),
                      label: 'Department',
                      value: review.dealArea,
                    },
                    {
                      icon: IconName.Money,
                      label: 'Size',
                      value: review.dealSize,
                    },
                    {
                      icon: IconName.CalendarToday,
                      label: 'Year',
                      value: review.dealDate,
                    },
                    {
                      icon: IconName.Sync,
                      label: 'Type',
                      value: review.dealType,
                    },
                  ].filter(({ value }) => value && value !== '-')
                  .map(({ icon, label, value }) => (
                    <div key={label}>
                      <Label>{label}</Label>
                      <Row>
                        <DynamicIcon
                          iconName={icon}
                          size="medium"
                          usePrimaryColor
                        />
                        <Value>{value}</Value>
                      </Row>
                    </div>
                  ))}
                </Grid2x2>
              </Section>

              <Divider orientation="vertical" flexItem />

              <AuthorSection className="review-card-author-section">
                <SectionTitle>Author</SectionTitle>
                <AuthorBadge
                  userId={review.userId}
                  user={review.user}
                  size="small"
                />
              </AuthorSection>

              <Divider orientation="vertical" flexItem />

              <ButtonColumn className="review-card-actions">
                {review.reviewDepth !== ReviewDepth.STUB && (
                  <Button
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      onGoToReview?.();
                    }}
                  >
                    Go to Deal Retro
                  </Button>
                )}
                <Button
                  size="small"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    onGotoCompany();
                  }}
                >
                  Go to Company
                </Button>
                <Button
                  data-testid="follow-company-button"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    followCompany();
                  }}
                  fullWidth
                >
                  {isFollowed ? 'Unfollow Company' : 'Follow Company'}
                </Button>
              </ButtonColumn>
            </CardRow>
          </CardContent>
        </StyledTypedCard>
      </motion.div>
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
};

export default ReviewCard;
